// Simple math renderer that converts LaTeX to HTML to eliminate flickering

const mathSymbols = {
  '\\sin': 'sin',
  '\\cos': 'cos',
  '\\tan': 'tan',
  '\\pi': 'π',
  '\\theta': 'θ',
  '\\alpha': 'α',
  '\\beta': 'β',
  '\\gamma': 'γ',
  '\\delta': 'δ',
  '\\epsilon': 'ε',
  '\\lambda': 'λ',
  '\\mu': 'μ',
  '\\sigma': 'σ',
  '\\phi': 'φ',
  '\\psi': 'ψ',
  '\\omega': 'ω',
  '\\infty': '∞',
  '\\pm': '±',
  '\\mp': '∓',
  '\\times': '×',
  '\\div': '÷',
  '\\neq': '≠',
  '\\leq': '≤',
  '\\geq': '≥',
  '\\approx': '≈',
  '\\equiv': '≡',
  '\\sqrt': '√',
  '\\sum': '∑',
  '\\prod': '∏',
  '\\int': '∫',
  '\\partial': '∂',
  '\\nabla': '∇',
  '\\Delta': 'Δ',
  '\\Sigma': 'Σ',
  '\\Omega': 'Ω',
  '\\degree': '°',
  '°': '°',
  '\\cdot': '·',
  '\\cdots': '⋯',
  '\\ldots': '…',
  '\\vdots': '⋮',
  '\\ddots': '⋱',
  '\\triangle': '△',
  '\\angle': '∠',
  '\\perp': '⊥',
  '\\parallel': '∥',
  '\\cong': '≅',
  '\\sim': '∼',
  '\\propto': '∝',
  '\\subset': '⊂',
  '\\supset': '⊃',
  '\\subseteq': '⊆',
  '\\supseteq': '⊇',
  '\\in': '∈',
  '\\notin': '∉',
  '\\cup': '∪',
  '\\cap': '∩',
  '\\emptyset': '∅',
  '\\forall': '∀',
  '\\exists': '∃',
  '\\nexists': '∄',
  '\\therefore': '∴',
  '\\because': '∵',
  '\\implies': '⟹',
  '\\iff': '⟺',
  '\\rightarrow': '→',
  '\\leftarrow': '←',
  '\\leftrightarrow': '↔',
  '\\Rightarrow': '⇒',
  '\\Leftarrow': '⇐',
  '\\Leftrightarrow': '⇔'
};

const renderFraction = (numerator, denominator) => {
  return `<span class="fraction">
    <span class="numerator">${numerator}</span>
    <span class="denominator">${denominator}</span>
  </span>`;
};

const renderSuperscript = (base, exponent) => {
  return `${base}<sup>${exponent}</sup>`;
};

const renderSubscript = (base, subscript) => {
  return `${base}<sub>${subscript}</sub>`;
};

const renderSqrt = (content) => {
  return `<span class="sqrt">√<span class="sqrt-content">${content}</span></span>`;
};

const renderWidehat = (content) => {
  return `<span class="widehat"><span class="widehat-content">${content}</span></span>`;
};

// Helper function to process LaTeX commands in a single line (used in cases environment)
const processLineLatex = (line) => {
  let processed = line;
  
  // Normalize double backslashes to single
  processed = processed.replace(/\\\\/g, '\\');
  
  // Normalize spaces around backslashes
  processed = processed.replace(/\s+\\([a-zA-Z]+)/g, '\\$1');
  processed = processed.replace(/\\\s+([a-zA-Z]+)/g, '\\$1');
  
  // Handle widehat (arc symbol)
  processed = processed.replace(/\\widehat\s*\{([^}]+)\}/g, (match, content) => {
    return renderWidehat(content);
  });
  processed = processed.replace(/\\widehat\s*([a-zA-Z0-9]+)/g, (match, content) => {
    return renderWidehat(content);
  });
  
  // Process math symbols (especially \leq and \geq for inequalities)
  Object.entries(mathSymbols).forEach(([latex, html]) => {
    const escapedLatex = latex.replace(/\\/g, '\\\\');
    const regex = new RegExp(escapedLatex + '(?![a-zA-Z])', 'g');
    processed = processed.replace(regex, html);
  });
  
  // Process superscripts and subscripts
  processed = processed.replace(/([a-zA-Z0-9\)]+)\^\{([^}]+)\}/g, (match, base, exp) => {
    return `${base}<sup>${exp}</sup>`;
  });
  processed = processed.replace(/([a-zA-Z0-9\)]+)\^([a-zA-Z0-9])/g, (match, base, exp) => {
    return `${base}<sup>${exp}</sup>`;
  });
  processed = processed.replace(/([a-zA-Z0-9\)]+)_\{([^}]+)\}/g, (match, base, sub) => {
    return `${base}<sub>${sub}</sub>`;
  });
  processed = processed.replace(/([a-zA-Z0-9\)]+)_([a-zA-Z0-9])/g, (match, base, sub) => {
    return `${base}<sub>${sub}</sub>`;
  });
  
  // Remove braces
  processed = processed.replace(/\{([^}]+)\}/g, '$1');
  
  // Clean up leftover backslashes
  processed = processed.replace(/\\\s+/g, ' ');
  processed = processed.replace(/\\(?![a-zA-Z_])/g, '');
  
  return processed;
};

const renderCases = (content) => {
  // Parse the cases content and format it as a system of equations
  // Split on LaTeX line breaks: In LaTeX cases environment, line breaks are indicated by \\ (double backslash)
  // In JavaScript strings, this appears as "\\\\" (4 backslashes in code = 2 literal backslashes)
  // We need to split on actual line breaks, not on single backslashes that are part of commands like \leq
  
  let lines;
  
  // Handle different scenarios for line breaks:
  // 1. Original format with \\\\ (4 backslashes in JS string = 2 literal backslashes = LaTeX line break)
  // 2. Already normalized format (shouldn't happen since cases is processed first, but handle it)
  // 3. Actual newline characters
  
  // Split on LaTeX line breaks: \\ (double backslash)
  // In JavaScript string, \\ is stored as literal backslash character
  // In regex pattern, \\ matches one backslash, so \\\\ matches two backslashes
  
  // First try: split on \\ (double backslash) which is the standard LaTeX line break
  // Pattern: \\ followed by optional whitespace (but not part of another command)
  lines = content.split(/\\\\\s*/).map(line => line.trim()).filter(line => line);
  
  // If that didn't work (only got one line), try alternative patterns
  if (lines.length <= 1) {
    // Try splitting on newline characters
    if (content.includes('\n')) {
      lines = content.split(/\n/).map(line => line.trim()).filter(line => line);
    }
    // If still one line, check if equations are concatenated (e.g., "=104p" means "=10" and "4p")
    else if (lines.length === 1 && lines[0].match(/=\d+[a-zA-Z]/)) {
      // Try to intelligently split concatenated equations
      // Pattern: look for "=number" followed immediately by letter (like "=104p")
      const text = lines[0];
      const parts = [];
      let lastIndex = 0;
      
      // Find patterns like "=10" followed by "4p" (the 4 got attached)
      const match = text.match(/(.+?=\d+)([a-zA-Z].*)/);
      if (match) {
        parts.push(match[1].trim()); // First equation: "pq + 2q = 10"
        parts.push(match[2].trim()); // Second equation: "4p + q = 14"
        lines = parts.filter(line => line);
      }
    }
  }
  
  // Process each line to handle LaTeX commands BEFORE wrapping in divs
  // This ensures inequalities like "x - 21 \leq 0" stay on one line
  const formattedLines = lines.map(line => {
    const processedLine = processLineLatex(line);
    return `<div class="case-line">${processedLine}</div>`;
  }).join('');
  
  // Use a proper left curly brace character and better structure
  return `<span class="cases">
    <span class="cases-left-brace">{</span>
    <span class="cases-content">${formattedLines}</span>
  </span>`;
};

// Helper function to find matching brace
const findMatchingBrace = (text, startIndex) => {
  let braceCount = 0;
  for (let i = startIndex; i < text.length; i++) {
    if (text[i] === '{') {
      braceCount++;
    } else if (text[i] === '}') {
      braceCount--;
      if (braceCount === 0) {
        return i;
      }
    }
  }
  return -1; // No matching brace found
};

export const renderMathToHTML = (mathText) => {
  if (!mathText || typeof mathText !== 'string') {
    return mathText;
  }

  let result = mathText;

  // Handle cases environment VERY FIRST - before removing dollar signs
  // This ensures it processes $\begin{cases}...\end{cases}$ correctly
  // Handle: $\begin{cases}...\end{cases}$, \begin{cases}...\end{cases}, $\\begin{cases}...\\end{cases}$, etc.
  result = result.replace(/\$?\s*(\\{1,2})begin\s*\{cases\}(.*?)(\\{1,2})end\s*\{cases\}\s*\$?/gs, (match, beginSlash, content, endSlash) => {
    return renderCases(content);
  });

  // Handle widehat (arc symbol) - before removing dollar signs
  // This ensures it processes $\widehat{AC}$ correctly
  // Handle: $\widehat{AC}$, \widehat{AC}, $\\widehat{AC}$ (with double backslash), etc.
  // Match pattern: optional $, optional spaces, 1-2 backslashes, widehat, optional spaces, {content}, optional spaces, optional $
  result = result.replace(/\$?\s*(\\{1,2})widehat\s*\{([^}]+)\}\s*\$?/g, (match, backslashes, content) => {
    return renderWidehat(content);
  });
  // Handle \widehat followed by letters/numbers without braces (e.g., $\widehatAC$, \widehatAC)
  result = result.replace(/\$?\s*(\\{1,2})widehat\s*([a-zA-Z0-9]+)\s*\$?/g, (match, backslashes, content) => {
    return renderWidehat(content);
  });

  // Normalize spaces around dollar signs to ensure consistency
  // Ensure consistent spacing before LaTeX expressions: "若$x" or "若 $x" both become "若 [content]"
  
  // Step 1: Handle cases where there's no space before $ (like "若$x" -> "若 $x")
  result = result.replace(/([^\s$])\$([^$]+)\$/g, '$1 $2'); // "若$x" -> "若 x" (adds space, removes $)
  
  // Step 2: Handle cases where there IS a space before $ (like "若 $x" -> preserve space)
  // Remove dollar signs while preserving the space: "若 $x$" -> "若 x"
  result = result.replace(/\s+\$([^$]+)\$/g, ' $1'); // " $x$" -> " x" (preserves space)
  
  // Step 3: Remove any remaining dollar signs (standalone or edge cases)
  result = result.replace(/\$([^$]+)\$/g, '$1'); // "$x$" -> "x" (no space context)
  result = result.replace(/^\$|\$$/g, ''); // Remove leading/trailing dollar signs
  
  // Step 4: Clean up other LaTeX delimiters
  result = result.replace(/\\\(|\\\)/g, '');
  result = result.replace(/\$/g, ''); // Remove all remaining dollar signs
  result = result.replace(/\\\[|\\\]/g, '');

  // Cases environment is already handled earlier (before dollar sign removal)
  // Catch any remaining cases that might have been missed
  result = result.replace(/(\\{1,2})begin\s*\{cases\}(.*?)(\\{1,2})end\s*\{cases\}/gs, (match, beginSlash, content, endSlash) => {
    return renderCases(content);
  });

  // Normalize LaTeX commands: remove spaces between backslash and command name
  // This fixes cases like "\ \alpha", " \alpha", or "\alpha" to "\alpha"
  // Also handle double backslashes that might come from database: "\\frac" -> "\frac"
  result = result.replace(/\\\\/g, '\\'); // Normalize double backslashes to single
  
  // First, handle cases with space before the backslash: " \alpha" -> "\alpha"
  result = result.replace(/\s+\\([a-zA-Z]+)/g, '\\$1');
  // Then, handle cases with space after backslash before command: "\ alpha" -> "\alpha"
  result = result.replace(/\\\s+([a-zA-Z]+)/g, '\\$1');

  // Handle LaTeX functions with subscripts FIRST (e.g., \log_a, \log_b) before general processing
  // This prevents backslashes from remaining visible
  const latexFunctions = ['log', 'ln', 'lg', 'sin', 'cos', 'tan', 'sec', 'csc', 'cot'];
  latexFunctions.forEach(func => {
    // Handle \log_a, \log_b, etc. - match \log_{a} or \log_a
    const funcRegex = new RegExp(`\\\\${func}_\\{([^}]+)\\}|\\\\${func}_([a-zA-Z0-9]+)`, 'g');
    result = result.replace(funcRegex, (match, braced, simple) => {
      const subscript = braced || simple;
      return `${func}<sub>${subscript}</sub>`;
    });
    // Handle functions without subscripts: \log, \ln, etc.
    const funcNoSubRegex = new RegExp(`\\\\${func}(?![a-zA-Z_])`, 'g');
    result = result.replace(funcNoSubRegex, func);
  });

  // Replace math symbols early (before other processing that might interfere)
  // This ensures Greek letters and other symbols are rendered correctly
  Object.entries(mathSymbols).forEach(([latex, html]) => {
    // Escape backslashes for regex
    const escapedLatex = latex.replace(/\\/g, '\\\\');
    // Create regex that matches the LaTeX command as a whole word to avoid partial matches
    const regex = new RegExp(escapedLatex + '(?![a-zA-Z])', 'g');
    result = result.replace(regex, html);
  });

  // Cases environment is already handled earlier, but catch any remaining ones
  result = result.replace(/\\begin\{cases\}(.*?)\\end\{cases\}/gs, (match, content) => {
    return renderCases(content);
  });

  // Handle fractions: \frac{a}{b} with proper brace matching
  // Process fractions BEFORE removing braces to preserve nested structures
  let fracIndex;
  let iterationCount = 0;
  const maxIterations = 100; // Prevent infinite loops
  
  while ((fracIndex = result.indexOf('\\frac')) !== -1 && iterationCount < maxIterations) {
    iterationCount++;
    const afterFrac = result.substring(fracIndex + 5); // 5 is length of '\frac'
    
    // Find the opening brace after \frac
    const openBraceIndex = afterFrac.indexOf('{');
    if (openBraceIndex === -1) break;
    
    // Find the matching closing brace for numerator
    const numEndIndex = findMatchingBrace(afterFrac, openBraceIndex);
    if (numEndIndex === -1) break;
    
    // Find the opening brace for denominator
    const denStartIndex = afterFrac.indexOf('{', numEndIndex + 1);
    if (denStartIndex === -1) break;
    
    // Find the matching closing brace for denominator
    const denEndIndex = findMatchingBrace(afterFrac, denStartIndex);
    if (denEndIndex === -1) break;
    
    let numerator = afterFrac.substring(openBraceIndex + 1, numEndIndex);
    let denominator = afterFrac.substring(denStartIndex + 1, denEndIndex);
    
    // Process numerator and denominator content (symbols, functions, etc.) but avoid infinite recursion
    // Process trigonometric functions and symbols that might be in numerator/denominator
    const latexFunctions = ['log', 'ln', 'lg', 'sin', 'cos', 'tan', 'sec', 'csc', 'cot'];
    latexFunctions.forEach(func => {
      const funcRegex = new RegExp(`\\\\${func}_\\{([^}]+)\\}|\\\\${func}_([a-zA-Z0-9]+)|\\\\${func}(?![a-zA-Z_])`, 'g');
      numerator = numerator.replace(funcRegex, (match, braced, simple) => {
        if (braced || simple) return `${func}<sub>${braced || simple}</sub>`;
        return func;
      });
      denominator = denominator.replace(funcRegex, (match, braced, simple) => {
        if (braced || simple) return `${func}<sub>${braced || simple}</sub>`;
        return func;
      });
    });
    
    // Process math symbols in numerator and denominator
    Object.entries(mathSymbols).forEach(([latex, html]) => {
      const escapedLatex = latex.replace(/\\/g, '\\\\');
      const regex = new RegExp(escapedLatex + '(?![a-zA-Z])', 'g');
      numerator = numerator.replace(regex, html);
      denominator = denominator.replace(regex, html);
    });
    
    // Process superscripts and subscripts in numerator and denominator
    numerator = numerator.replace(/([a-zA-Z0-9\)]+)\^\{([^}]+)\}/g, (match, base, exp) => {
      return `${base}<sup>${exp}</sup>`;
    });
    numerator = numerator.replace(/([a-zA-Z0-9\)]+)\^([a-zA-Z0-9])/g, (match, base, exp) => {
      return `${base}<sup>${exp}</sup>`;
    });
    numerator = numerator.replace(/([a-zA-Z0-9\)]+)_\{([^}]+)\}/g, (match, base, sub) => {
      return `${base}<sub>${sub}</sub>`;
    });
    numerator = numerator.replace(/([a-zA-Z0-9\)]+)_([a-zA-Z0-9])/g, (match, base, sub) => {
      return `${base}<sub>${sub}</sub>`;
    });
    
    denominator = denominator.replace(/([a-zA-Z0-9\)]+)\^\{([^}]+)\}/g, (match, base, exp) => {
      return `${base}<sup>${exp}</sup>`;
    });
    denominator = denominator.replace(/([a-zA-Z0-9\)]+)\^([a-zA-Z0-9])/g, (match, base, exp) => {
      return `${base}<sup>${exp}</sup>`;
    });
    denominator = denominator.replace(/([a-zA-Z0-9\)]+)_\{([^}]+)\}/g, (match, base, sub) => {
      return `${base}<sub>${sub}</sub>`;
    });
    denominator = denominator.replace(/([a-zA-Z0-9\)]+)_([a-zA-Z0-9])/g, (match, base, sub) => {
      return `${base}<sub>${sub}</sub>`;
    });
    
    // Remove braces that are no longer needed after processing
    numerator = numerator.replace(/\{([^}]+)\}/g, '$1');
    denominator = denominator.replace(/\{([^}]+)\}/g, '$1');
    
    // Calculate the full match length: '\frac' + '{' + numerator + '}' + '{' + denominator + '}'
    const fullMatchLength = 5 + denEndIndex + 1; // '\frac' (5) + position after closing brace
    
    // Replace the fraction
    const replacement = renderFraction(numerator, denominator);
    
    result = result.substring(0, fracIndex) + replacement + result.substring(fracIndex + fullMatchLength);
  }

  // Handle square roots: \sqrt{content}
  result = result.replace(/\\sqrt\{([^}]+)\}/g, (match, content) => {
    return renderSqrt(content);
  });

  // Handle superscripts: x^{2} or x^2
  result = result.replace(/([a-zA-Z0-9\)]+)\^\{([^}]+)\}/g, (match, base, exp) => {
    return renderSuperscript(base, exp);
  });
  result = result.replace(/([a-zA-Z0-9\)]+)\^([a-zA-Z0-9])/g, (match, base, exp) => {
    return renderSuperscript(base, exp);
  });

  // Handle subscripts: x_{2} or x_2
  result = result.replace(/([a-zA-Z0-9\)]+)_\{([^}]+)\}/g, (match, base, sub) => {
    return renderSubscript(base, sub);
  });
  result = result.replace(/([a-zA-Z0-9\)]+)_([a-zA-Z0-9])/g, (match, base, sub) => {
    return renderSubscript(base, sub);
  });

  // Note: Math symbols are already replaced earlier, right after normalization

  // Add spacing around operators and symbols for better readability
  // This ensures proper spacing between letters/words and mathematical symbols
  const operators = ['=', '+', '-', '×', '÷', '±', '∓', '≤', '≥', '≠', '≈', '≡', ':', '·'];
  operators.forEach(op => {
    // Escape special regex characters
    const escapedOp = op.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Add space before operator if there's no space already and it's not at the start
    // Pattern: (letter/number/Chinese/right paren/right bracket) + operator (no space)
    const beforeRegex = new RegExp(`([a-zA-Z0-9\u4e00-\u9fff\\)\\]°])(${escapedOp})(?!\\s)`, 'g');
    result = result.replace(beforeRegex, '$1 $2');
    
    // Add space after operator if there's no space already
    // Pattern: operator + (letter/number/Chinese/left paren/left bracket) (no space)
    const afterRegex = new RegExp(`(${escapedOp})([a-zA-Z0-9\u4e00-\u9fff\\(\\[])`, 'g');
    result = result.replace(afterRegex, '$1 $2');
  });

  // Handle simple cases
  result = result.replace(/\{([^}]+)\}/g, '$1'); // Remove remaining braces
  
  // Clean up backslashes with spaces: "\ b" -> "b" or "\  " -> " " or "\ " -> " "
  result = result.replace(/\\\s+/g, ' ');

  // Clean up any leftover standalone backslashes that aren't part of valid LaTeX commands
  // This handles cases where backslashes might remain after processing
  // Match: backslash not followed by a letter (not a LaTeX command) or underscore
  result = result.replace(/\\(?![a-zA-Z_])/g, '');

  // Handle line breaks: \n to <br> (process this last to avoid interference)
  result = result.replace(/\\n/g, '<br>');

  return result;
};

export const MathRenderer = ({ children, className = '', style = {} }) => {
  const renderedHTML = renderMathToHTML(children);
  
  return (
    <span 
      className={`math-rendered ${className}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: renderedHTML }}
    />
  );
}; 